import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Card, CardContent } from "ui/card";
import { Button } from "ui/button";
import { InputFieldset } from "ui/inputFieldset";
import { SelectFieldset } from "ui/selectFieldset";
import { SelectTriggerUI } from "ui/selectTrigger";
import { MultiSelect } from "ui/propertymulti-select";
import { Label } from "ui/label";
import { Badge } from "ui/badge";
import {
  Plus,
  X,
  Building2,
  Calendar,
  DoorOpen,
  CalendarCheck,
} from "lucide-react";
import { Select, SelectContent, SelectItem, SelectValue } from "ui/select";
import { toast } from "sonner";
import {
  getProperties,
  PropertyApi,
  getRoomTypes,
  RoomTypeApi,
  PropertyDetailsApi,
  PropertyRoomTypeApi,
  getPropertyById,
  getTourOperatorTypes,
  TourOperatorTypeApi,
  CreatePropertyAvailabilityPayload,
  updatePropertyAvailability,
  getPropertyAvailabilityById,
  releasePropertyAvailability,
} from "services/propertyAvailabilityService";
import { FullScreenLoading } from "components/ui/loader";
import { usePageHeader } from "contexts/PageHeaderContext";

interface DateRange {
  id: string;
  startDate: string;
  endDate: string;
}

interface RoomConfig {
  id: string;
  roomType: string;
  numberOfRooms: number;
  availableRooms?: number;
}

const AvailabilityEdit = () => {
  const navigate = useNavigate();
  const { setPageHeader } = usePageHeader();
  const today = new Date().toISOString().split("T")[0];
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { publicId } = useParams();

  const [properties, setProperties] = useState<PropertyApi[]>([]);
  const [loadingProperties, setLoadingProperties] = useState(false);
  const [roomTypes, setRoomTypes] = useState<RoomTypeApi[]>([]);
  const [tourOperatorTypes, setTourOperatorTypes] = useState<
    TourOperatorTypeApi[]
  >([]);
  const [loadingTourOperatorTypes, setLoadingTourOperatorTypes] =
    useState(false);
  const [loadingPropertyDetails, setLoadingPropertyDetails] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedPropertyDetails, setSelectedPropertyDetails] =
    useState<PropertyDetailsApi | null>(null);
  const [propertyRoomTypes, setPropertyRoomTypes] = useState<
    PropertyRoomTypeApi[]
  >([]);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [availabilityData, setAvailabilityData] = useState<any>(null);

  const [availabilityStatus, setAvailabilityStatus] = useState({
    isActive: true,
    isReleased: false,
  });

  const [formData, setFormData] = useState({
    propertyName: "",
    dateRanges: [] as DateRange[],
    roomConfigs: [] as RoomConfig[],
    availableTo: [] as string[],
    releaseDate: "",
    releaseAvailability: false,
  });

  const [errors, setErrors] = useState({
    propertyName: "",
    dateRanges: "",
    startDate: "",
    endDate: "",
    roomConfigs: "",
    availableTo: "",
  });

  const [tempRoomConfig, setTempRoomConfig] = useState({
    roomType: "",
    numberOfRooms: 1,
  });

  const [tempDateRange, setTempDateRange] = useState({
    startDate: "",
    endDate: "",
  });

  const getPropertyType = (propertyName: string): string => {
    const property = properties.find((p) => p.propertyName === propertyName);

    return property?.propertyTypeName || "";
  };

  const getPropertyTypeColor = (type: string) => {
    switch (type) {
      case "Faiz E Husaini ":
        return "bg-green-100 text-green-700 border-green-300";

      case "Hotel":
        return "bg-blue-100 text-blue-700 border-blue-300";

      case "Apartment":
        return "bg-purple-100 text-purple-700 border-purple-300";

      default:
        return "bg-gray-100 text-gray-700 border-gray-300";
    }
  };

  const resetForm = () => {
    setFormData({
      propertyName: "",
      dateRanges: [],
      roomConfigs: [],
      availableTo: [],
      releaseDate: "",
      releaseAvailability: false,
    });

    setTempDateRange({
      startDate: "",
      endDate: "",
    });

    setTempRoomConfig({
      roomType: "",
      numberOfRooms: 1,
    });
  };

  useEffect(() => {
    setPageHeader({
      title: "Edit Property Availability",
      icon: <CalendarCheck className="h-4 w-4" />,
      onBack: () => navigate("/property-availability"),
    });

    return () => setPageHeader(null);
  }, [navigate, setPageHeader]);

  useEffect(() => {
    fetchProperties();
    fetchRoomTypes();
    fetchTourOperatorTypes();
  }, []);

  useEffect(() => {
    if (publicId && properties.length > 0) {
      loadAvailability();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publicId, properties]);

  const fetchProperties = async () => {
    try {
      setLoadingProperties(true);

      const response = await getProperties();

      // optional: show only active properties
      const activeProperties = response.filter((property) => property.isActive);

      setProperties(activeProperties);
    } catch (error) {
      console.error("Failed to fetch properties:", error);

      toast.error("Failed to load properties");
    } finally {
      setLoadingProperties(false);
    }
  };

  const fetchPropertyDetails = async (propertyName: string) => {
    try {
      setLoadingPropertyDetails(true);

      const selectedProperty = properties.find(
        (p) => p.propertyName === propertyName,
      );

      if (!selectedProperty) return;

      const response = await getPropertyById(selectedProperty.publicId);

      setSelectedPropertyDetails(response);

      setPropertyRoomTypes(response.roomTypes || []);

      const propertyRoomTypeNames =
        response.roomTypes?.map((rt) => rt.roomTypeName) || [];

      if (propertyRoomTypeNames.length > 0 && !availabilityData) {
        setTempRoomConfig((prev) => ({
          ...prev,
          roomType: propertyRoomTypeNames[0],
        }));
      }
    } catch (error) {
      console.error("Failed to fetch property details:", error);

      toast.error("Failed to load property details");
    } finally {
      setLoadingPropertyDetails(false);
    }
  };

  const fetchRoomTypes = async () => {
    try {
      const response = await getRoomTypes();
      const activeRoomTypes = response.filter((roomType) => roomType.isActive);

      setRoomTypes(activeRoomTypes);
    } catch (error) {
      console.error("Failed to fetch room types:", error);

      toast.error("Failed to load room types");
    }
  };

  const fetchTourOperatorTypes = async () => {
    try {
      setLoadingTourOperatorTypes(true);

      const response = await getTourOperatorTypes();

      const activeTourOperatorTypes = response.filter((type) => type.isActive);

      setTourOperatorTypes(activeTourOperatorTypes);
    } catch (error) {
      console.error("Failed to fetch TO types:", error);

      toast.error("Failed to load TO types");
    } finally {
      setLoadingTourOperatorTypes(false);
    }
  };

  const loadAvailability = async () => {
    try {
      setLoadingPropertyDetails(true);

      const data =
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        await getPropertyAvailabilityById(publicId!);
      setAvailabilityData(data);

      setAvailabilityStatus({
        isActive: data.isActive,
        isReleased: data.isReleased,
      });

      setFormData({
        propertyName: data.propertyName,

        dateRanges: data.dateRanges.map(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (range: any, index: number) => ({
            id: `dr-${index}`,
            startDate: range.startDate.split("T")[0],
            endDate: range.endDate.split("T")[0],
          }),
        ),

        roomConfigs: data.roomConfigs.map(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (room: any, index: number) => ({
            id: `rc-${index}`,
            roomType: room.roomTypeName,
            numberOfRooms: room.numberOfRooms,
          }),
        ),

        availableTo: data.availableTourOperatorTypeIds.map(String),

        releaseDate: "",

        releaseAvailability: data.isReleased,
      });

      await fetchPropertyDetails(data.propertyName);
    } catch (error) {
      console.error(error);

      toast.error("Failed to load availability");
    } finally {
      setLoadingPropertyDetails(false);
    }
  };

  useEffect(() => {
    if (!availabilityData) return;

    setFormData((prev) => ({
      ...prev,

      roomConfigs: availabilityData.roomConfigs.map(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (room: any, index: number) => ({
          id: `rc-${index}`,
          roomType: room.roomTypeName,
          numberOfRooms: room.numberOfRooms,

          availableRooms: room.availableRooms ?? room.numberOfRooms,
        }),
      ),
    }));

    setTempRoomConfig({
      roomType: availabilityData.roomConfigs[0]?.roomTypeName || "",
      numberOfRooms: availabilityData.roomConfigs[0]?.numberOfRooms || 1,
    });
  }, [availabilityData]);

  const handleUpdate = async () => {
    const newErrors = {
      propertyName: "",
      dateRanges: "",
      startDate: "",
      endDate: "",
      roomConfigs: "",
      availableTo: "",
    };

    let hasError = false;

    if (!formData.propertyName) {
      newErrors.propertyName = "Property name is required";
      hasError = true;
    }

    if (formData.dateRanges.length === 0) {
      newErrors.dateRanges = "At least one date range is required";
      hasError = true;
    }

    if (formData.roomConfigs.length === 0) {
      newErrors.roomConfigs = "At least one room configuration is required";
      hasError = true;
    }

    if (formData.availableTo.length === 0) {
      newErrors.availableTo = "Please select at least one TO type";
      hasError = true;
    }

    setErrors(newErrors);

    if (hasError) {
      toast.error("Please fill all required fields");
      return;
    }

    try {
      setSaving(true);

      if (!selectedPropertyDetails) {
        toast.error("Property details not found");
        return;
      }

      if (!publicId) {
        toast.error("Availability ID missing");
        return;
      }

      const payload: CreatePropertyAvailabilityPayload = {
        propertyId: selectedPropertyDetails.propertyId,

        dateRanges: formData.dateRanges.map((range) => ({
          startDate: `${range.startDate}T00:00:00`,
          endDate: `${range.endDate}T00:00:00`,

          overrides: [],
        })),

        roomConfigs: formData.roomConfigs.map((config) => {
          const matchedRoomType = propertyRoomTypes.find(
            (rt) => rt.roomTypeName === config.roomType,
          );

          return {
            propertyRoomTypeId: matchedRoomType?.propertyRoomTypeId || 0,

            roomPublicId: matchedRoomType?.publicId || "",

            roomTypeName: config.roomType,

            numberOfRooms: config.numberOfRooms,
          };
        }),

        availableTourOperatorTypeIds: formData.availableTo.map(Number),
        isActive: availabilityStatus.isActive,
        isReleased: availabilityStatus.isReleased,
      };

      await updatePropertyAvailability(publicId, payload);

      toast.success("Property availability updated successfully");

      resetForm();

      navigate("/property-availability");
    } catch (error) {
      console.error("Failed to update property availability:", error);

      toast.error("Failed to update property availability");
    } finally {
      setSaving(false);
    }
  };

  const handleReleaseAvailability = async () => {
    try {
      if (!publicId) {
        toast.error("Availability ID missing");
        return;
      }

      setSaving(true);

      await releasePropertyAvailability(publicId);

      setAvailabilityStatus((prev) => ({
        ...prev,
        isReleased: true,
      }));

      toast.success("Availability released successfully");
    } catch (error) {
      console.error(error);

      toast.error("Failed to release availability");
    } finally {
      setSaving(false);
    }
  };

  const addDateRangeInline = () => {
    if (!tempDateRange.startDate) {
      setErrors({
        ...errors,
        startDate: "Please select start date",
      });

      return;
    }

    if (!tempDateRange.endDate) {
      setErrors({
        ...errors,
        endDate: "Please select end date",
      });

      return;
    }

    if (new Date(tempDateRange.endDate) <= new Date(tempDateRange.startDate)) {
      setErrors({
        ...errors,
        endDate: "End date must be after start date",
      });

      return;
    }

    const newDateRange: DateRange = {
      id: `dr-${Date.now()}`,
      startDate: tempDateRange.startDate,
      endDate: tempDateRange.endDate,
    };

    setFormData({
      ...formData,
      dateRanges: [...formData.dateRanges, newDateRange],
    });

    setTempDateRange({
      startDate: "",
      endDate: "",
    });

    setErrors({
      ...errors,
      dateRanges: "",
      startDate: "",
      endDate: "",
    });
  };

  const removeDateRange = (id: string) => {
    setFormData({
      ...formData,
      dateRanges: formData.dateRanges.filter((dr) => dr.id !== id),
    });
  };

  const addRoomConfig = () => {
    if (!tempRoomConfig.roomType) {
      toast.error("Please select room type");
      return;
    }

    if (!formData.propertyName) {
      toast.error("Please select property first");
      return;
    }

    const selectedRoomType = propertyRoomTypes.find(
      (rt) => rt.roomTypeName === tempRoomConfig.roomType,
    );

    const totalRooms = selectedRoomType?.numberOfRooms || 0;

    const alreadyAddedRooms = formData.roomConfigs
      .filter((rc) => rc.roomType === tempRoomConfig.roomType)
      .reduce((sum, rc) => sum + rc.numberOfRooms, 0);

    const remainingRooms = totalRooms - alreadyAddedRooms;

    if (remainingRooms <= 0) {
      toast.error(`No rooms available for ${tempRoomConfig.roomType}`);

      return;
    }

    if (tempRoomConfig.numberOfRooms > remainingRooms) {
      toast.error(
        `Only ${remainingRooms} rooms remaining for ${tempRoomConfig.roomType}`,
      );

      return;
    }

    const newRoomConfig: RoomConfig = {
      id: `rc-${Date.now()}`,
      roomType: tempRoomConfig.roomType,
      numberOfRooms: tempRoomConfig.numberOfRooms,
      availableRooms: remainingRooms - tempRoomConfig.numberOfRooms,
    };

    setFormData({
      ...formData,
      roomConfigs: [...formData.roomConfigs, newRoomConfig],
    });

    setTempRoomConfig({
      roomType: tempRoomConfig.roomType,
      numberOfRooms: 1,
    });

    setErrors({
      ...errors,
      roomConfigs: "",
    });
  };

  const removeRoomConfig = (id: string) => {
    setFormData({
      ...formData,
      roomConfigs: formData.roomConfigs.filter((rc) => rc.id !== id),
    });
  };

  const isReadOnly = availabilityStatus.isReleased;

  return (
    <>
      {(loadingProperties ||
        loadingTourOperatorTypes ||
        loadingPropertyDetails ||
        saving) && <FullScreenLoading />}
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <p className="text-gray-600">
              Update availability and booking period information
            </p>

            <Badge className="w-fit border border-red-200 bg-red-100 px-2.5 py-1 text-xs font-medium text-red-700">
              Applicable only for instant booking
            </Badge>
          </div>
          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => navigate("/property-availability")}
            >
              Cancel
            </Button>

            {!availabilityStatus.isReleased && (
              <Button
                onClick={handleUpdate}
                disabled={saving}
                style={{ background: "#5B9BD5" }}
                className="text-white"
              >
                {saving ? "Updating..." : "Update Availability"}
              </Button>
            )}
          </div>
        </div>
        <Card
          className="bg-white border-[#5B9BD5]/30"
          style={{ width: "80%", margin: "auto" }}
        >
          <CardContent style={{ padding: "10px 16px 16px 16px" }}>
            <div className="grid gap-4">
              {/* Property Name */}
              <div className="space-y-2">
                <SelectFieldset label="Property Name" required>
                  <Select
                    value={formData.propertyName}
                    disabled={true}
                    onValueChange={async (value) => {
                      setFormData({
                        ...formData,
                        propertyName: value,
                        roomConfigs: [],
                      });

                      setTempRoomConfig({
                        roomType: "",
                        numberOfRooms: 1,
                      });

                      setErrors({
                        ...errors,
                        propertyName: "",
                      });

                      await fetchPropertyDetails(value);
                    }}
                  >
                    <SelectTriggerUI>
                      <SelectValue
                        placeholder={
                          loadingProperties
                            ? "Loading properties..."
                            : "Select property"
                        }
                      />
                    </SelectTriggerUI>
                    <SelectContent>
                      {properties.map((property) => (
                        <SelectItem
                          key={property.publicId}
                          value={property.propertyName}
                        >
                          <div className="flex items-center gap-2">
                            <Building2 className="w-4 h-4 text-green-600" />
                            {property.propertyName} ({property.propertyTypeName}
                            )
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </SelectFieldset>
                {errors.propertyName && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.propertyName}
                  </p>
                )}
                {/* Property Type Badge */}
                {formData.propertyName && (
                  <div className="flex items-center gap-2">
                    <Badge
                      className={getPropertyTypeColor(
                        getPropertyType(formData.propertyName),
                      )}
                    >
                      {getPropertyType(formData.propertyName)} Property
                    </Badge>
                  </div>
                )}
              </div>

              {formData.propertyName && (
                <>
                  {/* Date Ranges - Inline */}
                  <div className="space-y-2">
                    <Label className="font-semibold">
                      Date Ranges <span className="text-red-500">*</span>
                    </Label>

                    {/* Add Date Range Inline */}
                    <div className="p-3 border rounded-lg bg-gray-50 space-y-3">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <InputFieldset
                            label="Start Date"
                            id="startDate"
                            type="date"
                            disabled={isReadOnly}
                            min={today}
                            value={tempDateRange.startDate}
                            onChange={(e) => {
                              const newStartDate = e.target.value;

                              setTempDateRange({
                                ...tempDateRange,
                                startDate: newStartDate,

                                endDate:
                                  tempDateRange.endDate &&
                                  new Date(tempDateRange.endDate) <=
                                    new Date(newStartDate)
                                    ? ""
                                    : tempDateRange.endDate,
                              });

                              setErrors({
                                ...errors,
                                startDate: "",
                                endDate: "",
                              });
                            }}
                          />
                          {errors.startDate && (
                            <p className="text-red-500 text-xs">
                              {errors.startDate}
                            </p>
                          )}
                        </div>
                        <div className="space-y-1">
                          <InputFieldset
                            label="End Date"
                            id="endDate"
                            type="date"
                            disabled={isReadOnly}
                            min={tempDateRange.startDate || today}
                            value={tempDateRange.endDate}
                            onChange={(e) => {
                              setTempDateRange({
                                ...tempDateRange,
                                endDate: e.target.value,
                              });

                              setErrors({
                                ...errors,
                                endDate: "",
                              });
                            }}
                          />
                          {errors.endDate && (
                            <p className="text-red-500 text-xs">
                              {errors.endDate}
                            </p>
                          )}
                        </div>
                      </div>
                      <Button
                        type="button"
                        onClick={addDateRangeInline}
                        variant="outline"
                        size="sm"
                        className="w-full"
                        style={{ color: "#5B9BD5", borderColor: "#5B9BD5" }}
                        disabled={isReadOnly}
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Add Date Range
                      </Button>
                    </div>

                    {/* List of Added Date Ranges */}
                    <div className="border rounded-lg p-3 bg-white space-y-2">
                      <Label className="text-sm font-medium">
                        Added Date Ranges ({formData.dateRanges.length})
                      </Label>
                      {formData.dateRanges.length === 0 ? (
                        <p className="text-sm text-gray-500 text-center py-2">
                          No date ranges added yet
                        </p>
                      ) : (
                        formData.dateRanges.map((range, index) => (
                          <div
                            key={range.id}
                            className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-2 border rounded bg-sky-50/50"
                          >
                            <div className="flex items-center gap-2">
                              <span
                                className="font-semibold text-xs"
                                style={{ color: "#5B9BD5" }}
                              >
                                #{index + 1}
                              </span>
                              <Calendar
                                className="w-3 h-3"
                                style={{ color: "#5B9BD5" }}
                              />
                              <span className="text-sm">
                                {new Date(range.startDate).toLocaleDateString()}{" "}
                                - {new Date(range.endDate).toLocaleDateString()}
                              </span>
                            </div>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              disabled={isReadOnly}
                              onClick={() => removeDateRange(range.id)}
                              className="h-7 px-2 text-red-600 hover:bg-red-50"
                            >
                              <X className="w-3 h-3" />
                            </Button>
                          </div>
                        ))
                      )}
                    </div>
                    {errors.dateRanges && (
                      <p className="text-red-500 text-xs">
                        {errors.dateRanges}
                      </p>
                    )}
                  </div>

                  {/* Room Configurations - Inline */}
                  <div className="space-y-2">
                    <Label className="font-semibold">
                      Room Configurations{" "}
                      <span className="text-red-500">*</span>
                    </Label>

                    {/* Add Room Config Inline */}
                    <div className="p-3 border rounded-lg bg-gray-50 space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <SelectFieldset label="Room Type">
                            <Select
                              value={tempRoomConfig.roomType}
                              onValueChange={(value) => {
                                const isAllowed = propertyRoomTypes.some(
                                  (prt) => prt.roomTypeName === value,
                                );

                                if (!isAllowed) {
                                  toast.error(
                                    "Selected room type is not available for this property",
                                  );

                                  return;
                                }

                                setTempRoomConfig({
                                  ...tempRoomConfig,
                                  roomType: value,
                                });
                              }}
                              disabled={!formData.propertyName || isReadOnly}
                            >
                              <SelectTriggerUI>
                                <SelectValue placeholder="Select room type" />
                              </SelectTriggerUI>
                              <SelectContent>
                                {roomTypes.map((roomType) => {
                                  const isAllowed = propertyRoomTypes.some(
                                    (prt) =>
                                      prt.roomTypeName === roomType.roomName,
                                  );

                                  return (
                                    <SelectItem
                                      key={roomType.publicId}
                                      value={roomType.roomName}
                                      disabled={!isAllowed}
                                      className={
                                        !isAllowed
                                          ? "opacity-50 cursor-not-allowed"
                                          : ""
                                      }
                                    >
                                      <div className="flex items-center justify-between w-full">
                                        <span>{roomType.roomName}</span>

                                        {/* {!isAllowed && (
                                          <span className="text-xs text-red-500 ml-2">
                                            Not Available
                                          </span>
                                        )} */}
                                      </div>
                                    </SelectItem>
                                  );
                                })}
                              </SelectContent>
                            </Select>
                          </SelectFieldset>
                        </div>
                        <div className="space-y-1">
                          <InputFieldset
                            label={
                              window.innerWidth < 640 ? "Rooms" : "No. of Rooms"
                            }
                            id="numberOfRooms"
                            type="number"
                            min="1"
                            value={tempRoomConfig.numberOfRooms}
                            onChange={(e) =>
                              setTempRoomConfig({
                                ...tempRoomConfig,
                                numberOfRooms: parseInt(e.target.value) || 1,
                              })
                            }
                            disabled={!formData.propertyName || isReadOnly}
                          />
                        </div>
                      </div>
                      <Button
                        type="button"
                        onClick={addRoomConfig}
                        variant="outline"
                        size="sm"
                        className="w-full"
                        style={{ color: "#5B9BD5", borderColor: "#5B9BD5" }}
                        disabled={!formData.propertyName || isReadOnly}
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Add Room Configuration
                      </Button>
                      {!formData.propertyName && (
                        <p className="text-xs text-gray-500">
                          Please select a property first
                        </p>
                      )}
                    </div>

                    {/* List of Added Room Configs */}
                    <div className="border rounded-lg p-3 bg-white space-y-2">
                      <Label className="text-sm font-medium">
                        Added Room Configurations ({formData.roomConfigs.length}
                        )
                      </Label>
                      {formData.roomConfigs.length === 0 ? (
                        <p className="text-sm text-gray-500 text-center py-2">
                          No room configurations added yet
                        </p>
                      ) : (
                        formData.roomConfigs.map((rc, index) => (
                          <div
                            key={rc.id}
                            className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-2 border rounded bg-sky-50/50"
                          >
                            <div className="flex items-center gap-2">
                              <span
                                className="font-semibold text-xs"
                                style={{ color: "#5B9BD5" }}
                              >
                                #{index + 1}
                              </span>
                              <DoorOpen
                                className="w-3 h-3"
                                style={{ color: "#5B9BD5" }}
                              />
                              <div className="text-sm">
                                <span className="font-medium">
                                  {rc.roomType}
                                </span>
                                <span className="text-gray-500"> - </span>
                                <span
                                  className="font-semibold"
                                  style={{ color: "#5B9BD5" }}
                                >
                                  {rc.numberOfRooms} rooms
                                </span>
                                <span className="text-gray-500">
                                  {" "}
                                  ({rc.availableRooms} available)
                                </span>
                              </div>
                            </div>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              disabled={isReadOnly}
                              onClick={() => removeRoomConfig(rc.id)}
                              className="h-7 px-2 text-red-600 hover:bg-red-50"
                            >
                              <X className="w-3 h-3" />
                            </Button>
                          </div>
                        ))
                      )}
                    </div>
                    {errors.roomConfigs && (
                      <p className="text-red-500 text-xs">
                        {errors.roomConfigs}
                      </p>
                    )}
                  </div>

                  {/* Available To - Multiselect */}
                  <div className="space-y-2">
                    <Label>Available to</Label>
                    <MultiSelect
                      options={tourOperatorTypes.map((type) => ({
                        value: String(type.tourOperatorTypeId),
                        label: type.typeName,
                      }))}
                      selected={formData.availableTo}
                      onChange={(selected) => {
                        setFormData({ ...formData, availableTo: selected });

                        setErrors({
                          ...errors,
                          availableTo: "",
                        });
                      }}
                      disabled={isReadOnly}
                      placeholder={
                        loadingTourOperatorTypes
                          ? "Loading TO types..."
                          : "Select TO types..."
                      }
                    />
                    {errors.availableTo && (
                      <p className="text-red-500 text-xs mt-1">
                        {errors.availableTo}
                      </p>
                    )}
                  </div>

                  {/* Release Availability Switch */}
                  <div className="flex justify-end">
                    {!availabilityStatus.isReleased && (
                      <Button
                        onClick={handleReleaseAvailability}
                        disabled={saving}
                        style={{ background: "#5B9BD5" }}
                        className="text-white"
                      >
                        Release Availability
                      </Button>
                    )}
                  </div>
                </>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );
};

export default AvailabilityEdit;
